import { supabase } from '@/lib/supabase'

// ── TournamentManagement.jsx ──

export async function fetchTournaments({ searchTerm, sportId, status, startDateFrom, startDateTo }) {
  const { data, error } = await supabase.rpc('get_tournaments', {
    p_search_term: searchTerm || null,
    p_sport_id: sportId || null,
    p_status: status || null,
    p_start_date_from: startDateFrom || null,
    p_start_date_to: startDateTo || null
  })
  if (error) throw error
  return data
}

export async function fetchSports() {
  const { data, error } = await supabase
    .from('sports')
    .select('id, name')
    .eq('is_active', true)
    .order('name')
  if (error) throw error
  return sortSportsPickleballFirst(data)
}

// Owners asked for Pickleball to be the first option in any sport picker.
// Sort client-side because the `sports` table has no display_order column.
function sortSportsPickleballFirst(rows) {
  if (!Array.isArray(rows)) return rows
  const isPickleball = (r) => (r?.name || '').trim().toLowerCase() === 'pickleball'
  return [...rows].sort((a, b) => {
    const aPB = isPickleball(a)
    const bPB = isPickleball(b)
    if (aPB && !bPB) return -1
    if (!aPB && bPB) return 1
    return (a?.name || '').localeCompare(b?.name || '')
  })
}

export async function fetchTournamentFavorites({ userId }) {
  const { data, error } = await supabase
    .from('tournament_favorites')
    .select('tournament_id')
    .eq('user_id', userId)
  if (error) throw error
  return data
}

export async function addTournamentFavorite({ userId, tournamentId }) {
  const { data, error } = await supabase
    .from('tournament_favorites')
    .insert({ user_id: userId, tournament_id: tournamentId })
  if (error) throw error
  return data
}

export async function removeTournamentFavorite({ userId, tournamentId }) {
  const { data, error } = await supabase
    .from('tournament_favorites')
    .delete()
    .eq('user_id', userId)
    .eq('tournament_id', tournamentId)
  if (error) throw error
  return data
}

// ── TournamentView.jsx ──

export async function fetchTournament({ tournamentId }) {
  const { data, error } = await supabase.rpc('get_tournament', {
    tournament_id_param: tournamentId
  })
  if (error) throw error
  return data
}

export async function fetchUserBracketEntries({ userId, bracketIds }) {
  const { data, error } = await supabase
    .from('bracket_entries')
    .select('id, bracket_id, total_score, potential_points, accuracy, champion_status, champion_team_id')
    .eq('user_id', userId)
    .in('bracket_id', bracketIds)
  if (error) throw error
  const names = await fetchTeamNames(data?.map(e => e.champion_team_id) || [])
  return data?.map(e => ({ ...e, champion_team_name: names[e.champion_team_id] || null }))
}

// bracket_teams.player_ids → "A / B", in player order. Same name format as
// get_leaderboard_rankings' champion_team_name, so a bracket card and the
// division leaderboard print the champ identically (ticket 22405f8c).
export async function fetchTeamNames(teamIds) {
  const ids = [...new Set(teamIds.filter(Boolean))]
  if (ids.length === 0) return {}
  const { data: teams, error } = await supabase
    .from('bracket_teams')
    .select('id, player_ids')
    .in('id', ids)
  if (error) throw error
  const playerIds = [...new Set(teams.flatMap(t => t.player_ids || []))]
  if (playerIds.length === 0) return {}
  const { data: players, error: playersError } = await supabase
    .from('players')
    .select('id, name')
    .in('id', playerIds)
  if (playersError) throw playersError
  const playerName = Object.fromEntries(players.map(p => [p.id, p.name]))
  return Object.fromEntries(teams.map(t => [
    t.id,
    (t.player_ids || []).map(id => playerName[id]).filter(Boolean).join(' / ') || null,
  ]))
}

export async function fetchUserBracketStats({ userId, bracketIds }) {
  const { data, error } = await supabase
    .from('bracket_entries')
    .select('total_score, potential_points, champion_status')
    .eq('user_id', userId)
    .in('bracket_id', bracketIds)
    .is('group_id', null)
  if (error) throw error
  return data
}

export async function fetchLeaderboardRankings({ groupId, sportId, tournamentId, divisionId, bracketId, year, limit = 100 }) {
  const { data, error } = await supabase.rpc('get_leaderboard_rankings', {
    p_group_id: groupId || null,
    p_sport_id: sportId || null,
    p_tournament_id: tournamentId || null,
    p_division_id: divisionId || null,
    p_bracket_id: bracketId || null,
    p_year: year || null,
    p_limit: limit
  })
  if (error) throw error
  return data
}

export async function updateTournament({ tournamentId, name, startDate, endDate, submissionDeadline, status, isTest }) {
  const { data, error } = await supabase.rpc('update_tournament', {
    tournament_id_param: tournamentId,
    name_param: name,
    start_date_param: startDate,
    end_date_param: endDate,
    submission_deadline_param: submissionDeadline,
    status_param: status,
    is_test_param: isTest ?? null,
  })
  if (error) throw error
  return data
}

export async function deleteTournament({ tournamentId }) {
  const { data, error } = await supabase.rpc('delete_tournament', {
    tournament_id_param: tournamentId
  })
  if (error) throw error
  return data
}

export async function deleteBracket({ bracketId }) {
  const { data, error } = await supabase.rpc('delete_bracket', {
    bracket_id_param: bracketId
  })
  if (error) throw error
  return data
}

// ── TournamentCreate.jsx ──

export async function createTournament({ name, sportId, startDate, endDate, submissionDeadline, isTest = false }) {
  const { data, error } = await supabase.rpc('create_tournament', {
    name_param: name,
    sport_id_param: sportId,
    start_date_param: startDate,
    end_date_param: endDate,
    submission_deadline_param: submissionDeadline,
    is_test_param: isTest,
  })
  if (error) throw error
  return data
}

// ── TournamentPrizes.jsx ──

export async function fetchPrizes({ tournamentId, searchTerm, groupId, hasWinner, asPlayer = false }) {
  const { data, error } = await supabase.rpc('get_prizes', {
    p_tournament_id: tournamentId,
    p_search_term: searchTerm || null,
    p_group_id: groupId || null,
    p_has_winner: hasWinner ?? null,
    p_as_player: asPlayer,
  })
  if (error) throw error
  return data
}

// ── AddBracketModal.jsx ──

export async function fetchDivisionsBySport({ sportId }) {
  const { data, error } = await supabase.rpc('get_divisions_by_sport', {
    sport_id_param: sportId
  })
  if (error) throw error
  return data
}

export async function createBracket({ tournamentId, divisionId, format = 'single_elim' }) {
  const { data, error } = await supabase.rpc('create_bracket', {
    tournament_id_param: tournamentId,
    division_id_param: divisionId,
    format_param: format,
  })
  if (error) throw error
  return data
}

// ── Admin: official entry management (TournamentEdit.jsx) ──

export async function fetchBracketOfficialEntries({ tournamentId }) {
  const { data, error } = await supabase
    .from('brackets')
    .select('id, official_entry_id')
    .eq('tournament_id', tournamentId)
  if (error) throw error
  return data
}

export async function createEntry({ bracketId, isOfficial = false, groupId = null }) {
  const { data, error } = await supabase.rpc('create_entry', {
    p_bracket_id: bracketId,
    p_is_official: isOfficial,
    p_group_id: groupId,
  })
  if (error) throw error
  return data
}
